/**
 * Template Field Mapping tRPC Input Schemas (Issue #1531, Phase 2 PR B)
 *
 * Zod input schemas for the `termsheets.templateMapping.*` tRPC procedures.
 * Each input refines `canonicalKey` against `VALID_CANONICAL_KEYS` so unknown
 * keys are rejected at the tRPC boundary — before they ever reach the
 * `34-crm-service` gRPC layer or the database.
 *
 * Companion DTO: `1-shared-types/src/dtos/template-field-mapping.dto.ts`.
 *
 * See:
 *   - docs/plans/20260429-PLAN-termsheet-phase-2-blueprint.md (Workstream B2,
 *     C1: tRPC router contract).
 */

import { z } from 'zod';
import { TemplateFieldTransformSchema, VALID_CANONICAL_KEYS } from '../dtos/template-field-mapping.dto';

// ============================================================================
// SHARED REFINEMENTS
// ============================================================================

/**
 * Canonical-key input — must exist in `LOAN_DATA_POINTS`. Reused by every
 * mutation that accepts a canonical key so error messages stay uniform and
 * the registry stays the single source of truth.
 */
const CanonicalKeySchema = z.string().refine((k) => VALID_CANONICAL_KEYS.includes(k), {
  message:
    'canonicalKey must exist in LOAN_DATA_POINTS or PARTNERSHIP_DATA_POINTS (see 1-shared-types/src/config/loan-data-points.ts and partnership-data-points.ts)',
});

// ============================================================================
// UPSERT
// ============================================================================

/**
 * Input for `termsheets.templateMapping.upsert`.
 *
 * Idempotent on `(templateId, docuSealFieldUuid)`. Repeated calls with the
 * same field UUID overwrite the existing binding (canonical key, transform,
 * isRequired, defaultValue) and update `updatedAt`.
 */
export const UpsertTemplateMappingInputSchema = z.object({
  templateId: z.string().uuid(),
  docuSealFieldUuid: z.string().min(1),
  canonicalKey: CanonicalKeySchema,
  transform: TemplateFieldTransformSchema.nullable().optional(),
  isRequired: z.boolean().optional().default(false),
  defaultValue: z.string().nullable().optional(),
});

export type UpsertTemplateMappingInput = z.infer<typeof UpsertTemplateMappingInputSchema>;

// ============================================================================
// LIST
// ============================================================================

/**
 * Input for `termsheets.templateMapping.list` — returns every mapping row
 * for the given template, scoped to the caller's `businessId`.
 */
export const ListTemplateMappingsInputSchema = z.object({
  templateId: z.string().uuid(),
});

export type ListTemplateMappingsInput = z.infer<typeof ListTemplateMappingsInputSchema>;

// ============================================================================
// DELETE
// ============================================================================

/**
 * Input for `termsheets.templateMapping.delete` — removes one binding by
 * `mappingId`. The router enforces `businessId` isolation before delegating
 * to the gRPC service.
 */
export const DeleteTemplateMappingInputSchema = z.object({
  mappingId: z.string().uuid(),
});

export type DeleteTemplateMappingInput = z.infer<typeof DeleteTemplateMappingInputSchema>;
